import type { MintedOutcome, RenewalTrigger, SessionOutcome } from './types';

const DEFAULT_PROACTIVE_LEAD_SECONDS = 300;
const DEFAULT_COOLDOWN_MS = 30_000;
const DEFAULT_RETRY_DELAY_MS = 15_000;

export interface RenewalPolicyOptions {
  proactiveLeadSeconds?: number;
  cooldownMs?: number;
  retryDelayMs?: number;
  now?: () => number;
}

export type RenewalResult =
  | { status: 'renewed'; minted: MintedOutcome }
  | { status: 'suppressed' }
  | { status: 'resolved'; outcome: SessionOutcome };

export class RenewalPolicy {
  private readonly leadMs: number;

  private readonly cooldownMs: number;

  private readonly retryDelayMs: number;

  private readonly now: () => number;

  private onFire: (() => void) | null = null;

  private expiryMs: number | null = null;

  private cooldownUntilMs: number | null = null;

  private timerHandle: ReturnType<typeof setTimeout> | null = null;

  private retryHandle: ReturnType<typeof setTimeout> | null = null;

  private inFlight: Promise<RenewalResult> | null = null;

  // Bumped by stop() so a runAttempt() already awaiting attempt() when a sibling-tab
  // logout or manual sign-out tears the session down resolves to suppressed instead of
  // re-arming a session that no longer exists.
  private epoch = 0;

  constructor(options: RenewalPolicyOptions = {}) {
    this.leadMs = (options.proactiveLeadSeconds ?? DEFAULT_PROACTIVE_LEAD_SECONDS) * 1000;
    this.cooldownMs = options.cooldownMs ?? DEFAULT_COOLDOWN_MS;
    this.retryDelayMs = options.retryDelayMs ?? DEFAULT_RETRY_DELAY_MS;
    this.now = options.now ?? (() => Date.now());
  }

  arm(minted: MintedOutcome, onFire: () => void): void {
    this.onFire = onFire;
    this.recordMinted(minted);
    this.scheduleTimer(minted.expiresIn);
  }

  async request(trigger: RenewalTrigger, attempt: () => Promise<SessionOutcome>): Promise<RenewalResult> {
    if (trigger === 'unauthorized' && this.withinCooldown()) {
      return { status: 'suppressed' };
    }
    if (this.inFlight) {
      return this.inFlight;
    }
    this.inFlight = this.runAttempt(attempt);
    try {
      return await this.inFlight;
    } finally {
      this.inFlight = null;
    }
  }

  stop(): void {
    this.teardown();
    this.epoch += 1;
  }

  private async runAttempt(attempt: () => Promise<SessionOutcome>): Promise<RenewalResult> {
    const epoch = this.epoch;
    const outcome = await attempt();
    if (epoch !== this.epoch) {
      return { status: 'suppressed' };
    }

    if (outcome.kind === 'minted') {
      this.clearRetryTimer();
      this.recordMinted(outcome);
      this.scheduleTimer(outcome.expiresIn);
      this.cooldownUntilMs = this.now() + this.cooldownMs;
      return { status: 'renewed', minted: outcome };
    }

    if (outcome.kind === 'unavailable' && this.heldTokenStillValid()) {
      this.scheduleRetry();
      this.cooldownUntilMs = this.now() + this.cooldownMs;
      return { status: 'suppressed' };
    }

    this.teardown();
    return { status: 'resolved', outcome };
  }

  private teardown(): void {
    this.clearTimer();
    this.clearRetryTimer();
    this.expiryMs = null;
    this.cooldownUntilMs = null;
  }

  private recordMinted(minted: MintedOutcome): void {
    this.expiryMs = this.now() + minted.expiresIn * 1000;
  }

  private scheduleTimer(expiresIn: number): void {
    this.clearTimer();
    const delay = Math.max(expiresIn * 1000 - this.leadMs, this.retryDelayMs);
    this.timerHandle = setTimeout(() => {
      this.onFire?.();
    }, delay);
  }

  private scheduleRetry(): void {
    this.clearRetryTimer();
    this.retryHandle = setTimeout(() => {
      this.onFire?.();
    }, this.retryDelayMs);
  }

  private heldTokenStillValid(): boolean {
    return this.expiryMs !== null && this.expiryMs > this.now();
  }

  private withinCooldown(): boolean {
    return this.cooldownUntilMs !== null && this.cooldownUntilMs > this.now();
  }

  private clearTimer(): void {
    if (this.timerHandle !== null) {
      clearTimeout(this.timerHandle);
      this.timerHandle = null;
    }
  }

  private clearRetryTimer(): void {
    if (this.retryHandle !== null) {
      clearTimeout(this.retryHandle);
      this.retryHandle = null;
    }
  }
}
